import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  FormControlLabel,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Grid,
  Snackbar,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';

export default function BannedIps() {
  const [banned, setBanned] = useState([]);
  const [showExpired, setShowExpired] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newIp, setNewIp] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bRes, eRes] = await Promise.all([
        api.get('/banned_ips', { params: { showExpired } }),
        api.get('/banned_ips/excluded')
      ]);
      setBanned(bRes.data);
      setExcluded(eRes.data);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error cargando datos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [showExpired]);

  const handleRelease = async ip => {
    if (!window.confirm(`Liberar IP ${ip}?`)) return;
    try {
      await api.delete(`/banned_ips/${ip}`);
      setSnackbar({ open: true, message: 'IP liberada', severity: 'info' });
      fetchData();
    } catch {
      setSnackbar({ open: true, message: 'Error liberando IP', severity: 'error' });
    }
  };

  const handleDeleteExcluded = async ip => {
    if (!window.confirm(`Eliminar IP excluida ${ip}?`)) return;
    try {
      await api.delete(`/banned_ips/excluded/${ip}`);
      setSnackbar({ open: true, message: 'IP excluida eliminada', severity: 'info' });
      fetchData();
    } catch {
      setSnackbar({ open: true, message: 'Error eliminando excluida', severity: 'error' });
    }
  };

  const handleAddExcluded = async () => {
    if (!newIp) {
      setSnackbar({ open: true, message: 'IP es obligatoria', severity: 'warning' });
      return;
    }
    try {
      await api.post('/banned_ips/excluded', { ip_address: newIp, description: newDesc });
      setSnackbar({ open: true, message: 'IP excluida agregada', severity: 'success' });
      setOpenDialog(false);
      setNewIp(''); setNewDesc('');
      fetchData();
    } catch {
      setSnackbar({ open: true, message: 'Error agregando excluida', severity: 'error' });
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Gestión de IPs Bloqueadas</Typography>
      <Box display="flex" alignItems="center" mb={2}>
        <FormControlLabel
          control={<Switch checked={showExpired} onChange={() => setShowExpired(prev => !prev)} />}
          label="Mostrar expiradas"
        />
        <IconButton onClick={fetchData}><RefreshIcon /></IconButton>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center"><CircularProgress /></Box>
      ) : (
        <Paper sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>IP</TableCell>
                <TableCell>Razón</TableCell>
                <TableCell>Baneada</TableCell>
                <TableCell>Hasta</TableCell>
                <TableCell align="right">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {banned.map(row => (
                <TableRow key={row.ip_address} hover>
                  <TableCell>{row.ip_address}</TableCell>
                  <TableCell>{row.reason}</TableCell>
                  <TableCell>{new Date(row.banned_at).toLocaleString()}</TableCell>
                  <TableCell>{new Date(row.banned_until).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <IconButton color="error" onClick={() => handleRelease(row.ip_address)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography variant="h5" gutterBottom>IPs Excluidas</Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)} sx={{ mb: 1 }}>Agregar Excluida</Button>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>IP</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Creada</TableCell>
              <TableCell align="right">Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {excluded.map(row => (
              <TableRow key={row.ip_address} hover>
                <TableCell>{row.ip_address}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <IconButton color="error" onClick={() => handleDeleteExcluded(row.ip_address)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Agregar IP Excluida</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="IP Address"
            value={newIp}
            onChange={e => setNewIp(e.target.value)}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Descripción"
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddExcluded}>Agregar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
